import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, asset, formatUSD, CATEGORIES } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, Sparkles, Star, Upload, Link2, RefreshCw, Loader2, GripVertical, ChevronLeft, ChevronRight, RotateCcw, Check } from "lucide-react";
import { TEXT_SECTIONS, TEXT_DEFAULTS, useSiteText } from "../lib/siteText";
import { IncognitoStoriesAdmin } from "../components/admin/IncognitoStoriesAdmin";
import { LegacyAdmin } from "../components/admin/LegacyAdmin";
import { PlannersAdmin } from "../components/admin/PlannersAdmin";

const TABS = ["Overview", "Time-lapses", "Photos", "Major Events", "Hurricane Helene", "Orders", "Incognito", "Incognito Photos", "Incognito Stories", "Planners", "Legacy", "Page Text"];
const INC_STATUS = ["new", "contacted", "booked", "closed"];
const FULFILL = ["awaiting_payment", "pending_fulfillment", "in_production", "shipped", "delivered"];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("ll_admin") || "");
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(!!localStorage.getItem("ll_admin"));
  const [tab, setTab] = useState("Overview");

  const hdr = useCallback(() => ({ headers: { "X-Admin-Token": token } }), [token]);

  const login = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem("ll_admin", data.token);
      setToken(data.token); setAuthed(true);
      toast.success("Welcome back.");
    } catch { toast.error("Invalid password."); }
  };
  const logout = () => { localStorage.removeItem("ll_admin"); setAuthed(false); setToken(""); };

  useEffect(() => { document.title = "Studio Admin — Scott Burton Photography"; }, []);

  if (!authed) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-sm w-full">
        <div className="overline-label mb-3">Studio</div>
        <h1 className="font-serif text-4xl font-light mb-6">Admin sign-in</h1>
        <Input data-testid="admin-password" type="password" placeholder="Password" value={pw}
          onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} />
        <button data-testid="admin-login" onClick={login} className="w-full mt-4 btn-amber rounded-full py-3">Enter studio</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-5xl font-light">Studio</h1>
        <button onClick={logout} className="text-sm text-neutral-400 hover:text-white">Sign out</button>
      </div>
      <div className="mt-8 flex gap-1 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-3 text-sm whitespace-nowrap -mb-px border-b-2 ${tab === t ? "border-amber-glow text-amber-glow" : "border-transparent text-neutral-400 hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="mt-10">
        {tab === "Overview" && <Overview hdr={hdr} />}
        {tab === "Time-lapses" && <Timelapses hdr={hdr} token={token} />}
        {tab === "Photos" && <Photos hdr={hdr} token={token} />}
        {tab === "Major Events" && <EventsReview hdr={hdr} />}
        {tab === "Hurricane Helene" && <Helene hdr={hdr} token={token} />}
        {tab === "Orders" && <Orders hdr={hdr} />}
        {tab === "Incognito" && <IncognitoLeads hdr={hdr} />}
        {tab === "Incognito Photos" && <IncognitoPhotos hdr={hdr} />}
        {tab === "Incognito Stories" && <IncognitoStoriesAdmin hdr={hdr} />}
        {tab === "Planners" && <PlannersAdmin hdr={hdr} />}
        {tab === "Legacy" && <LegacyAdmin hdr={hdr} />}
        {tab === "Page Text" && <PageText hdr={hdr} />}
      </div>
    </div>
  );
}

const Card = ({ children }) => <div className="border border-white/10 rounded-lg p-6 bg-obsidian-800">{children}</div>;

function Overview({ hdr }) {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/stats`, hdr()).then(({ data }) => setS(data)).catch(() => {}); }, [hdr]);
  if (!s) return <div className="text-neutral-500">Loading…</div>;
  const items = [["Sequences", s.timelapses], ["Photos", s.photos], ["Orders", s.orders], ["Revenue", formatUSD(s.revenue)]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
      {items.map(([l, v]) => (
        <Card key={l}><div className="overline-label">{l}</div><div className="font-serif text-4xl mt-2 text-amber-glow">{v}</div></Card>
      ))}
    </div>
  );
}

function Timelapses({ hdr, token }) {
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState("url");
  const [form, setForm] = useState({ title: "", location: "", date: "", description: "", category: "Weather", tags: "", personal_price: 49, commercial_price: 299, capture_start_label: "", capture_end_label: "", video_url: "" });
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => axios.get(`${API}/timelapses`).then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { await axios.delete(`${API}/admin/timelapses/${id}`, hdr()); load(); toast.success("Deleted"); };
  const feature = async (id, f) => { await axios.patch(`${API}/admin/timelapses/${id}/feature?featured=${f}`, {}, hdr()); load(); };

  const submit = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      if (mode === "url") {
        if (!form.video_url) { toast.error("Video URL required"); setBusy(false); return; }
        await axios.post(`${API}/admin/timelapses/from-url`, {
          ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
          personal_price: Number(form.personal_price), commercial_price: Number(form.commercial_price),
        }, hdr());
        toast.success("Processing video → frames. It'll appear when ready.");
      } else {
        if (!files || !files.length) { toast.error("Select frame images"); setBusy(false); return; }
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => k !== "video_url" && fd.append(k, v));
        Array.from(files).forEach((f) => fd.append("files", f));
        await axios.post(`${API}/admin/timelapses/frames`, fd, { headers: { "X-Admin-Token": token } });
        toast.success("Sequence created");
      }
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <Card>
        <h3 className="font-serif text-2xl font-light mb-1">Add a time-lapse</h3>
        <div className="flex gap-2 my-4">
          <TabBtn active={mode === "url"} onClick={() => setMode("url")}><Link2 size={14} /> From video link</TabBtn>
          <TabBtn active={mode === "frames"} onClick={() => setMode("frames")}><Upload size={14} /> Upload frames</TabBtn>
        </div>
        <div className="space-y-3">
          <Input data-testid="tl-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <Input placeholder="Date (YYYY-MM-DD)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <Textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.filter((c) => c !== "All").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Capture start (e.g. 05:12 AM)" value={form.capture_start_label} onChange={(e) => setForm({ ...form, capture_start_label: e.target.value })} />
            <Input placeholder="Capture end (e.g. 06:58 AM)" value={form.capture_end_label} onChange={(e) => setForm({ ...form, capture_end_label: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Personal price" value={form.personal_price} onChange={(e) => setForm({ ...form, personal_price: e.target.value })} />
            <Input placeholder="Commercial price" value={form.commercial_price} onChange={(e) => setForm({ ...form, commercial_price: e.target.value })} />
          </div>
          {mode === "url"
            ? <Input data-testid="tl-video-url" placeholder="Direct video URL (mp4/mov)" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} />
            : <input data-testid="tl-frames-input" type="file" multiple accept="image/*" onChange={(e) => setFiles(e.target.files)} className="text-sm text-neutral-400" />}
          <button data-testid="tl-submit" onClick={submit} disabled={busy} className="btn-amber rounded-full px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Create sequence
          </button>
        </div>
      </Card>

      <div className="space-y-3">
        {items.map((tl) => (
          <div key={tl.id} data-testid={`admin-tl-${tl.id}`} className="flex items-center gap-3 border border-white/10 rounded-lg p-3 bg-obsidian-800">
            {tl.thumbnail_url && <img src={asset(tl.thumbnail_url)} alt="" className="w-16 h-16 object-cover rounded" />}
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg truncate">{tl.title}</div>
              <div className="text-xs text-neutral-500">{tl.category} · {tl.frame_count} frames · {tl.status}</div>
            </div>
            <button onClick={() => feature(tl.id, !tl.featured)} title="Feature" className={tl.featured ? "text-amber-glow" : "text-neutral-500 hover:text-white"}><Star size={16} fill={tl.featured ? "currentColor" : "none"} /></button>
            <button data-testid={`admin-tl-del-${tl.id}`} onClick={() => del(tl.id)} className="text-neutral-500 hover:text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Photos({ hdr, token }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: "Sunset", description: "", image_url: "", tags: "" });
  const [file, setFile] = useState(null);
  const load = useCallback(() => axios.get(`${API}/photos`).then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await axios.delete(`${API}/admin/photos/${id}`, hdr()); load(); toast.success("Removed"); };
  const add = async () => {
    try {
      if (file) {
        const fd = new FormData();
        fd.append("file", file); fd.append("title", form.title || file.name);
        fd.append("category", form.category); fd.append("description", form.description); fd.append("tags", form.tags);
        await axios.post(`${API}/admin/photos/upload`, fd, { headers: { "X-Admin-Token": token } });
      } else {
        if (!form.image_url || !form.title) { toast.error("Title + image URL required"); return; }
        await axios.post(`${API}/admin/photos`, { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) }, hdr());
      }
      toast.success("Photo added"); setForm({ title: "", category: "Sunset", description: "", image_url: "", tags: "" }); setFile(null); load();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <Card>
        <h3 className="font-serif text-2xl font-light mb-4">Add a photo</h3>
        <div className="space-y-3">
          <Input data-testid="photo-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.filter((c) => c !== "All").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          </div>
          <Input data-testid="photo-url" placeholder="Image URL (or upload below)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="text-sm text-neutral-400" />
          <button data-testid="photo-add" onClick={add} className="btn-amber rounded-full px-5 py-2.5 text-sm">Add photo</button>
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-3">
        {items.map((p) => (
          <div key={p.id} className="relative group rounded-lg overflow-hidden border border-white/10">
            <img src={asset(p.image_url)} alt="" className="w-full h-40 object-cover" />
            <button data-testid={`photo-del-${p.id}`} onClick={() => del(p.id)} className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5 text-neutral-300 hover:text-red-400"><Trash2 size={14} /></button>
            <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80 text-xs">{p.title}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Helene({ hdr, token }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ url: "", title: "", description: "", media_type: "image", edited: false });
  const [file, setFile] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(() => axios.get(`${API}/collections/hurricane-helene/items`).then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await axios.delete(`${API}/admin/collections/items/${id}`, hdr()); load(); toast.success("Removed"); };
  const describe = async (id) => {
    setBusyId(id);
    try { const { data } = await axios.post(`${API}/admin/collections/items/${id}/describe`, {}, hdr()); toast.success("Description generated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusyId(null);
  };
  const add = async () => {
    try {
      if (file) {
        const fd = new FormData();
        fd.append("file", file); fd.append("collection", "hurricane-helene");
        fd.append("title", form.title || file.name); fd.append("description", form.description); fd.append("edited", form.edited);
        await axios.post(`${API}/admin/collections/upload`, fd, { headers: { "X-Admin-Token": token } });
      } else {
        if (!form.url) { toast.error("URL required"); return; }
        await axios.post(`${API}/admin/collections/items`, { ...form, collection: "hurricane-helene" }, hdr());
      }
      toast.success("Added"); setForm({ url: "", title: "", description: "", media_type: "image", edited: false }); setFile(null); load();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <Card>
        <h3 className="font-serif text-2xl font-light mb-1">Hurricane Helene media</h3>
        <p className="text-neutral-500 text-sm mb-4">Add images or video by link or upload. AI can write a description for images.</p>
        <div className="space-y-3">
          <Input data-testid="helene-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="image">Image</SelectItem><SelectItem value="video">Video</SelectItem></SelectContent>
            </Select>
            <Select value={String(form.edited)} onValueChange={(v) => setForm({ ...form, edited: v === "true" })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="true">Edited</SelectItem><SelectItem value="false">Unedited</SelectItem></SelectContent>
            </Select>
          </div>
          <Input data-testid="helene-url" placeholder="Media URL (or upload below)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <input type="file" accept="image/*,video/*" onChange={(e) => setFile(e.target.files[0])} className="text-sm text-neutral-400" />
          <Textarea placeholder="Description (optional — AI can generate for images)" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button data-testid="helene-add" onClick={add} className="btn-amber rounded-full px-5 py-2.5 text-sm">Add to collection</button>
        </div>
      </Card>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`helene-item-${it.id}`} className="flex gap-3 border border-white/10 rounded-lg p-3 bg-obsidian-800">
            {it.media_type === "video"
              ? <video src={asset(it.url)} className="w-20 h-20 object-cover rounded bg-black" />
              : <img src={asset(it.url)} alt="" className="w-20 h-20 object-cover rounded" />}
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg truncate">{it.title}</div>
              <div className="text-xs text-neutral-500 line-clamp-2">{it.description || "No description"}</div>
              <div className="flex gap-3 mt-2">
                {it.media_type === "image" && (
                  <button data-testid={`helene-describe-${it.id}`} onClick={() => describe(it.id)} disabled={busyId === it.id} className="text-xs text-amber-glow flex items-center gap-1 hover:underline">
                    {busyId === it.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI describe
                  </button>
                )}
                <span className="text-xs text-neutral-600">{it.edited ? "Edited" : "Unedited"}</span>
              </div>
            </div>
            <button data-testid={`helene-del-${it.id}`} onClick={() => del(it.id)} className="text-neutral-500 hover:text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsReview({ hdr }) {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(() => axios.get(`${API}/collections/major-events/items`).then(({ data }) => {
    setItems(data);
    const d = {};
    data.forEach((it) => { d[it.id] = { title: it.title || "", description: it.description || "" }; });
    setDrafts(d);
  }), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, updates) => {
    setBusyId(id);
    try { await axios.patch(`${API}/admin/collections/items/${id}`, updates, hdr()); await load(); }
    catch { toast.error("Save failed"); }
    setBusyId(null);
  };
  const save = (id) => patch(id, drafts[id]);
  const toggleFeature = (it) => patch(it.id, { featured: !it.featured });
  const setDraft = (id, k, v) => setDrafts((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const [aiId, setAiId] = useState(null);
  const recaption = async (it) => {
    setAiId(it.id);
    try {
      const { data } = await axios.post(`${API}/admin/collections/items/${it.id}/recaption`, {}, hdr());
      setDrafts((p) => ({ ...p, [it.id]: { title: data.title || "", description: data.caption || "" } }));
      toast.success("AI suggestion ready — review, then Save");
    } catch { toast.error("AI caption failed"); }
    setAiId(null);
  };

  const featuredCount = items.filter((i) => i.featured).length;

  return (
    <div data-testid="events-review">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-2xl font-light">Major Events — review &amp; feature</h3>
          <p className="text-neutral-500 text-sm mt-1">Edit any caption, then tap the star to headline it on the Major Events page. {featuredCount} featured.</p>
        </div>
        <button onClick={load} className="text-sm text-neutral-400 flex items-center gap-1 hover:text-white"><RefreshCw size={13} /> Refresh</button>
      </div>
      <ReelOrder items={items.filter((i) => i.featured)} hdr={hdr} onSaved={load} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`event-item-${it.id}`} className={`border rounded-lg overflow-hidden bg-obsidian-800 ${it.featured ? "border-amber-glow/60" : "border-white/10"}`}>
            <div className="relative">
              {it.media_type === "video"
                ? <video src={asset(it.url)} className="w-full h-44 object-cover bg-black" />
                : <img src={asset(it.url)} alt="" className="w-full h-44 object-cover" />}
              <button data-testid={`event-feature-${it.id}`} onClick={() => toggleFeature(it)} title="Feature"
                className={`absolute top-2 right-2 rounded-full p-2 bg-black/70 ${it.featured ? "text-amber-glow" : "text-neutral-300 hover:text-white"}`}>
                <Star size={16} fill={it.featured ? "currentColor" : "none"} />
              </button>
            </div>
            <div className="p-3 space-y-2">
              <Input data-testid={`event-title-${it.id}`} placeholder="Title" value={drafts[it.id]?.title || ""}
                onChange={(e) => setDraft(it.id, "title", e.target.value)} className="text-sm" />
              <Textarea data-testid={`event-caption-${it.id}`} placeholder="Caption" rows={2} value={drafts[it.id]?.description || ""}
                onChange={(e) => setDraft(it.id, "description", e.target.value)} className="text-sm" />
              <div className="flex items-center gap-3">
                <button data-testid={`event-save-${it.id}`} onClick={() => save(it.id)} disabled={busyId === it.id}
                  className="btn-amber rounded-full px-4 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-60">
                  {busyId === it.id ? <Loader2 size={12} className="animate-spin" /> : null} Save
                </button>
                {it.media_type === "image" && (
                  <button data-testid={`event-ai-${it.id}`} onClick={() => recaption(it)} disabled={aiId === it.id}
                    className="text-xs text-amber-glow flex items-center gap-1 hover:underline disabled:opacity-60">
                    {aiId === it.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI re-describe
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders({ hdr }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => axios.get(`${API}/admin/orders`, hdr()).then(({ data }) => setItems(data)).catch(() => {}), [hdr]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, s) => { await axios.patch(`${API}/admin/orders/${id}`, { fulfillment_status: s }, hdr()); load(); };
  const fulfill = async (id) => { await axios.post(`${API}/admin/orders/${id}/fulfill`, {}, hdr()); load(); toast.success("Sent to Miller's (mock)"); };
  return (
    <div className="space-y-3" data-testid="admin-orders">
      <button onClick={load} className="text-sm text-neutral-400 flex items-center gap-1 hover:text-white"><RefreshCw size={13} /> Refresh</button>
      {items.length === 0 && <div className="text-neutral-500 py-10">No orders yet.</div>}
      {items.map((o) => (
        <div key={o.id} className="border border-white/10 rounded-lg p-4 bg-obsidian-800 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="font-mono text-xs text-neutral-500">{o.order_number} · {o.payment_status}</div>
            <div className="font-serif text-lg">{o.source_title}</div>
            <div className="text-xs text-neutral-400 capitalize">{o.type.replace("_", " ")} · {o.customer_email}</div>
          </div>
          <div className="font-mono text-amber-glow">{formatUSD(o.total)}</div>
          <Select value={o.fulfillment_status} onValueChange={(s) => setStatus(o.id, s)}>
            <SelectTrigger className="w-44" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{FULFILL.map((f) => <SelectItem key={f} value={f}>{f.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          {(o.type === "frame_print" || o.type === "photo_print") && (
            <button data-testid={`order-fulfill-${o.id}`} onClick={() => fulfill(o.id)} className="text-xs border border-white/15 rounded-full px-3 py-1.5 hover:border-amber-glow">
              {o.millers_order_id ? `Miller's ${o.millers_order_id}` : "Send to Miller's"}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function IncognitoLeads({ hdr }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => axios.get(`${API}/admin/incognito`, hdr()).then(({ data }) => setItems(data)).catch(() => {}), [hdr]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await axios.patch(`${API}/admin/incognito/${id}`, { status }, hdr()); load(); toast.success("Updated"); };
  return (
    <div className="space-y-3" data-testid="admin-incognito">
      <button onClick={load} data-testid="incognito-refresh" className="text-sm text-neutral-400 flex items-center gap-1 hover:text-white"><RefreshCw size={13} /> Refresh</button>
      {items.length === 0 && <div className="text-neutral-500 py-10">No Incognito requests yet.</div>}
      {items.map((r) => (
        <div key={r.id} data-testid={`incognito-lead-${r.id}`} className="border border-white/10 rounded-lg p-4 bg-obsidian-800 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="font-mono text-xs text-neutral-500">{new Date(r.created_at).toLocaleString()}</div>
            <div className="font-serif text-lg">{r.name}</div>
            <div className="text-xs text-neutral-400">
              {r.wedding_date || "No date"} · {r.venue || "No venue"} · <a href={`mailto:${r.email}`} className="hover:text-amber-glow">{r.email}</a>{r.phone ? ` · ${r.phone}` : ""}
            </div>
            {r.message && <div className="text-sm text-neutral-300 mt-2">{r.message}</div>}
            {r.ref && <div className="text-xs text-amber-glow mt-1">Referred by: {r.ref}</div>}
          </div>
          <Select value={r.status || "new"} onValueChange={(s) => setStatus(r.id, s)}>
            <SelectTrigger className="w-36 capitalize" data-testid={`incognito-status-${r.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{INC_STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

function ReelOrder({ items, hdr, onSaved }) {
  const [order, setOrder] = useState(items);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const key = items.map((i) => i.id).join(",");
  useEffect(() => { setOrder(items); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return null;

  const move = (from, to) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    setOrder(next);
  };
  const changed = order.map((i) => i.id).join(",") !== key;
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/collections/major-events/reorder`, { ids: order.map((i) => i.id) }, hdr());
      toast.success("Cover reel order saved");
      await onSaved();
    } catch { toast.error("Could not save order"); }
    setSaving(false);
  };

  return (
    <div data-testid="reel-order" className="mb-10 border border-white/10 rounded-lg p-5 bg-obsidian-800">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-xl font-light">Cover reel order</div>
          <p className="text-neutral-500 text-xs mt-1">Drag the photos (or use the arrows) into the order they should play, then save.</p>
        </div>
        <button data-testid="reel-order-save" onClick={save} disabled={!changed || saving}
          className="btn-amber rounded-full px-5 py-2 text-xs flex items-center gap-1.5 disabled:opacity-40">
          {saving && <Loader2 size={12} className="animate-spin" />} Save order
        </button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {order.map((it, i) => (
          <div key={it.id} data-testid={`reel-slot-${i}`} draggable
            onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
            className={`shrink-0 w-40 rounded-md border bg-obsidian cursor-grab active:cursor-grabbing transition-opacity ${dragIdx === i ? "opacity-40 border-amber-glow" : "border-white/10"}`}>
            <div className="relative">
              <img src={asset(it.url)} alt="" className="w-full h-24 object-cover rounded-t-md pointer-events-none" />
              <span className="absolute top-1 left-1 font-mono text-[10px] bg-black/70 rounded px-1.5 py-0.5 text-amber-glow">{i + 1}</span>
              <GripVertical size={14} className="absolute top-1 right-1 text-white/70" />
            </div>
            <div className="px-2 py-1.5 flex items-center justify-between gap-1">
              <button data-testid={`reel-left-${i}`} onClick={() => move(i, i - 1)} disabled={i === 0} className="text-neutral-400 hover:text-white disabled:opacity-20"><ChevronLeft size={14} /></button>
              <span className="text-[11px] text-neutral-400 truncate">{it.title || "Untitled"}</span>
              <button data-testid={`reel-right-${i}`} onClick={() => move(i, i + 1)} disabled={i === order.length - 1} className="text-neutral-400 hover:text-white disabled:opacity-20"><ChevronRight size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PageText({ hdr }) {
  const { texts, setTexts } = useSiteText();
  const [draft, setDraft] = useState({ ...TEXT_DEFAULTS, ...texts });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft({ ...TEXT_DEFAULTS, ...texts }); }, [texts]);

  const save = async () => {
    setSaving(true);
    const changed = Object.fromEntries(Object.entries(draft).filter(([k, v]) => v.trim() && v !== TEXT_DEFAULTS[k]));
    try {
      const { data } = await axios.put(`${API}/admin/content`, { texts: changed }, hdr());
      setTexts(data);
      toast.success("Page text saved — it's live on the site now");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="admin-page-text" className="space-y-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-neutral-400 text-sm max-w-xl">Edit the words on your pages. Press Enter for a new line. Use the reset arrow to go back to the original wording.</p>
        <button data-testid="page-text-save" onClick={save} disabled={saving}
          className="btn-amber rounded-full px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
          {saving && <Loader2 size={14} className="animate-spin" />} Save all text
        </button>
      </div>
      {TEXT_SECTIONS.map((sec) => (
        <div key={sec.page}>
          <h3 className="font-serif text-2xl font-light mb-5">{sec.page} page</h3>
          <div className="grid gap-5 md:grid-cols-2">
            {sec.fields.map(([k, label, def]) => (
              <div key={k}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-neutral-400">{label}</label>
                  {draft[k] !== def && (
                    <button data-testid={`page-text-reset-${k}`} title="Reset to original" onClick={() => setDraft((d) => ({ ...d, [k]: def }))}
                      className="text-neutral-500 hover:text-amber-glow"><RotateCcw size={12} /></button>
                  )}
                </div>
                <Textarea data-testid={`page-text-${k}`} rows={def.length > 90 ? 4 : 2} value={draft[k] || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} className="text-sm" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const shownOnSite = (p) => (p.incognito_pick ?? p.tele_ai) === true;

function IncognitoPhotos({ hdr }) {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("showing");
  const [building, setBuilding] = useState(false);
  const load = useCallback(() => axios.get(`${API}/admin/incognito/photos`, hdr()).then(({ data }) => setItems(data)).catch(() => {}), [hdr]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    const pick = !shownOnSite(p);
    setItems((all) => all.map((x) => (x.id === p.id ? { ...x, incognito_pick: pick } : x)));
    await axios.patch(`${API}/admin/incognito/photos/${p.id}`, { pick }, hdr()).catch(() => {
      setItems((all) => all.map((x) => (x.id === p.id ? p : x)));
      toast.error("Save failed");
    });
  };
  const buildShare = async () => {
    setBuilding(true);
    try { await axios.post(`${API}/admin/incognito/share-image`, {}, hdr()); toast.success("Share image updated"); }
    catch { toast.error("Could not build share image"); }
    setBuilding(false);
  };

  const showing = items.filter(shownOnSite);
  const unchecked = items.filter((p) => p.tele_ai === undefined || p.tele_ai === null).length;
  const list = view === "showing" ? showing : items.filter((p) => !shownOnSite(p));
  const shareUrl = `${window.location.origin}/api/share/incognito`;

  return (
    <div data-testid="admin-incognito-photos" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h3 className="font-serif text-2xl font-light">Long-lens photos for Incognito</h3>
          <p className="text-neutral-500 text-sm mt-1">
            AI pre-selected photos that look like telephoto candids. Tap a photo to remove it, or switch to "Not showing" and tap to add it.
            {unchecked > 0 && <span className="text-amber-glow"> AI is still checking {unchecked} photos — refresh later.</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="incognito-photos-refresh" className="text-sm text-neutral-400 flex items-center gap-1 hover:text-white"><RefreshCw size={13} /> Refresh</button>
          <button onClick={buildShare} disabled={building} data-testid="incognito-share-build" className="btn-amber rounded-full px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-60">
            {building && <Loader2 size={12} className="animate-spin" />} Update share image
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 border border-white/10 rounded-lg p-4 bg-obsidian-800 text-sm">
        <span className="text-neutral-400">Link to post on social (shows a rich preview):</span>
        <code data-testid="incognito-share-url" className="text-amber-glow break-all">{shareUrl}</code>
        <button data-testid="incognito-share-copy" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }}
          className="text-xs border border-white/15 rounded-full px-3 py-1 hover:border-amber-glow">Copy</button>
        <span className="text-neutral-500">· Short link for bios: <span className="text-neutral-300">{window.location.origin}/secret</span></span>
      </div>
      <div className="flex gap-2">
        <TabBtn active={view === "showing"} onClick={() => setView("showing")}>Showing on site ({showing.length})</TabBtn>
        <TabBtn active={view === "hidden"} onClick={() => setView("hidden")}>Not showing ({items.length - showing.length})</TabBtn>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
        {list.map((p) => (
          <button key={p.id} data-testid={`incognito-photo-${p.id}`} onClick={() => toggle(p)}
            className={`relative aspect-square overflow-hidden rounded border-2 transition-opacity ${shownOnSite(p) ? "border-amber-glow" : "border-transparent opacity-60 hover:opacity-100"}`}>
            <img src={asset(p.image_url)} alt="" loading="lazy" className="w-full h-full object-cover" />
            {shownOnSite(p) && <Check size={14} className="absolute top-1 right-1 text-amber-glow bg-black/70 rounded-full p-0.5" />}
          </button>
        ))}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 border ${active ? "border-amber-glow text-amber-glow" : "border-white/12 text-neutral-400"}`}>{children}</button>
);
